import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Col, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { ptShort } from 'yup-locale-pt';
import { LuGripVertical } from 'react-icons/lu';
import { LuArrowRight, LuCheckCircle, LuInfo, LuPencil, LuTrash2 } from 'react-icons/lu';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './form-fixes.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

import {
  Button,
  Card,
  CardBody,
  FormMaskedInput,
  FormSelectField,
  FormTextField,
  FormTextAreaField,
  FormDateField,
} from '_components/_core';

import { LoadingIcon } from '_components/_shared';

import { CustomFieldFormGroup, RequiredIndicator, FormGridContainer } from './styles';

Yup.setLocale(ptShort);

function EventRegistrationPage({
  registrationCount,
  isLoading,
  preview,
  previewEvent,
  onLayoutChange,
  onCreateRegistration,
  onDeleteField,
  onEditField,
  onFetchEventPublic,
}) {
  const { id } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showWaitlistSuccess, setShowWaitlistSuccess] = useState(false);
  const [validationSchema, setValidationSchema] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [remainingSpots, setRemainingSpots] = useState(0);

  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (preview && previewEvent) {
      setEvent(previewEvent);

      return;
    }

    if (id && !preview) {
      onFetchEventPublic(id, found_event => {
        setEvent(found_event);
      });
    }
  }, [id, onFetchEventPublic, previewEvent, preview]);

  useEffect(() => {
    if (event && registrationCount !== undefined) {
      setRemainingSpots(event.capacity - registrationCount);
    }
  }, [event, registrationCount]);

  useEffect(() => {
    if (event && event.custom_fields) {
      generateFormConfiguration(event.custom_fields);
    }
  }, [event]);

  const generateFormConfiguration = useCallback(customFields => {
    if (!customFields || !customFields.length) return;

    const values = {};

    customFields.forEach(field => {
      if (['heading', 'divider'].includes(field.type)) return;

      switch (field.type) {
        case 'checkbox':
          values[field.name] = [];
          break;
        case 'number':
          values[field.name] = 0;
          break;
        default:
          values[field.name] = '';
      }
    });

    setInitialValues(values);

    const schemaFields = {};

    customFields.forEach(field => {
      if (['heading', 'divider'].includes(field.type)) return;

      let fieldSchema;
      switch (field.type) {
        case 'text':
        case 'textarea':
          fieldSchema = Yup.string();
          break;
        case 'number':
          fieldSchema = Yup.number();
          break;
        case 'email':
          fieldSchema = Yup.string().email('Email inválido');
          break;
        case 'date':
          fieldSchema = Yup.date();
          break;
        case 'phone':
          fieldSchema = Yup.string();
          break;
        case 'select':
        case 'radio':
          fieldSchema = Yup.string();
          break;
        case 'checkbox':
          fieldSchema = Yup.array();
          break;
        default:
          fieldSchema = Yup.string();
      }

      if (field.required) {
        fieldSchema = fieldSchema.required('Campo obrigatório');
      }

      if (field.depends_on && field.depends_value) {
        schemaFields[field.name] = Yup.mixed().when(field.depends_on, {
          is: value => value === field.depends_value,
          then: fieldSchema,
          otherwise: field.required
            ? Yup.mixed().notRequired()
            : fieldSchema.notRequired(),
        });
      } else {
        schemaFields[field.name] = fieldSchema;
      }
    });

    setValidationSchema(Yup.object().shape(schemaFields));
  }, []);

  const handleCreateRegistration = async values => {
    try {
      setIsSubmitting(true);
      const status = remainingSpots > 0 ? 'registered' : 'waiting_list';

      const registrationData = {
        event_id: id,
        form_data: values,
      };

      const success = await onCreateRegistration(registrationData);

      setIsSubmitting(false);

      if (success) {
        if (status === 'registered') {
          setShowSuccess(true);
        } else {
          setShowWaitlistSuccess(true);
        }
      }
    } catch (err) {
      console.error('Error submitting registration:', err);
      setIsSubmitting(false);
    }
  };

  const handleFieldSelected = useCallback(
    (field, index) => {
      onEditField(field, index);
    },
    [onEditField],
  );

  const handleDeleteField = useCallback(
    (field, index) => {
      onDeleteField(field, index);
    },
    [onDeleteField],
  );

  const renderEditControls = useCallback(
    (field, index) => {
      return (
        <>
          <Button
            variant="white"
            className="m-0 p-0 p-1 d-flex align-items-center"
            onClick={() => {
              handleFieldSelected(field, index);
            }}
          >
            <LuPencil size={12} />
          </Button>
          <Button
            variant="white"
            className="m-0 p-0 p-1 d-flex align-items-center ml-1"
            onClick={() => {
              handleDeleteField(field, index);
            }}
          >
            <LuTrash2 size={12} className="text-danger" />
          </Button>
        </>
      );
    },
    [handleDeleteField, handleFieldSelected],
  );

  const renderDragHandle = useCallback(() => {
    return (
      <span
        className="drag-handle mr-2 d-flex align-items-center justify-content-center"
        style={{ cursor: 'move', color: '#6c757d' }}
      >
        <LuGripVertical />
      </span>
    );
  }, []);

  if (isLoading) {
    return (
      <Container className="my-5 d-flex justify-content-center">
        <Card className="p-4 w-100">
          <LoadingIcon text="Carregando informações do evento..." />
        </Card>
      </Container>
    );
  }

  // if (error) {
  //   return (
  //     <Container className="my-5 d-flex justify-content-center">
  //       <Card className="p-4 w-100">
  //         <CardBody className="text-center">
  //           <LuInfo size={50} color="#dc3545" />
  //           <h3 className="mt-3">Erro</h3>
  //           <p>{error}</p>
  //           <Button variant="primary" onClick={() => window.location.reload()}>
  //             Tentar novamente
  //           </Button>
  //         </CardBody>
  //       </Card>
  //     </Container>
  //   );
  // }

  if (showSuccess) {
    return (
      <Container className="my-5 d-flex justify-content-center">
        <Card
          className="p-4 w-100 d-flex align-items-center justify-content-center"
          style={{
            borderTop: '8px solid rgb(9, 173, 64)',
          }}
        >
          <LuCheckCircle size={50} color="#09ad40" />
          <h3 className="mt-3">Inscrição confirmada</h3>
          <p className="p-0 m-0 text-center mt-2">
            Sua inscrição foi realizada com sucesso! <br />
            Em breve entraremos em contato com mais informações.
          </p>
        </Card>
      </Container>
    );
  }

  if (showWaitlistSuccess) {
    return (
      <Container className="my-5 d-flex justify-content-center">
        <Card
          className="p-4 w-100 d-flex align-items-center justify-content-center"
          style={{
            borderTop: '8px solid #cec004',
          }}
        >
          <LuInfo size={50} color="#cec004" />
          <h3 className="mt-3">Adicionado na lista de espera</h3>
          <p className="p-0 m-0 text-center mt-2">
            Sua inscrição na lista de espera foi realizada! <br />
            <br />
            Entraremos em contato caso novas vagas fiquem disponíveis.
          </p>
        </Card>
      </Container>
    );
  }

  if (remainingSpots <= 0 && !event?.waiting_list_enabled) {
    return (
      <Container className="my-5 d-flex justify-content-center">
        <Card
          className="p-4 w-100 d-flex align-items-center justify-content-center"
          style={{
            borderTop: '8px solid rgb(80, 83, 81)',
          }}
        >
          <LuInfo size={50} color="#505351" />
          <h3 className="mt-3">Inscrições encerradas</h3>
          <p className="p-0 m-0 text-center mt-2">
            As inscrições para {event?.name} foram finalizadas pois o limite de
            participantes foi atingido.
          </p>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <>
        {/* <Card
            className="p-4"
            style={{
              borderTop: '8px solid rgb(96, 125, 139)',
            }}
          >
            <h3>Ficha de Inscrição</h3>
            <p className="mb-0">
              <b>{event.name}</b>
            </p>
            {event.address && (
              <p className="mb-0 mt-2">
                <b>Local:</b> {event.address}
              </p>
            )}
            <p className="mb-0 mt-2">
              <b>Data:</b> {new Date(event.start_date).toLocaleDateString()}
              {event.end_date &&
                event.end_date !== event.start_date &&
                ` a ${new Date(event.end_date).toLocaleDateString()}`}
            </p>
            {remainingSpots <= 0 && event.waiting_list_enabled && (
              <div className="alert alert-warning mt-3">
                Todas as vagas para este evento foram preenchidas. Sua inscrição será
                adicionada à lista de espera.
              </div>
            )}
          </Card> */}
        {/* <pre>{JSON.stringify(event.custom_fields, null, 2)}</pre> */}
        {/* <pre>{JSON.stringify(formBodyRef.current?.offsetWidth, null, 2)}</pre> */}

        {/* {validationSchema && ( */}
        <Card className="mt-3">
          <CardBody>
            <h5>Formulário de Inscrição</h5>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={handleCreateRegistration}
            >
              {({ values, handleSubmit, isValid, touched, errors, setFieldValue }) => {
                const sortFieldsByLayout = (fields, layout) => {
                  return [...fields].sort((a, b) => {
                    const layoutA = layout.find(
                      item => item.i === (a.field_id || a.name),
                    );
                    const layoutB = layout.find(
                      item => item.i === (b.field_id || b.name),
                    );

                    if (!layoutA || !layoutB) return 0;

                    if (layoutA.y !== layoutB.y) {
                      return layoutA.y - layoutB.y;
                    }

                    return layoutA.x - layoutB.x;
                  });
                };

                const sortedFields = sortFieldsByLayout(
                  event.custom_fields,
                  event.form_layout,
                );

                return (
                  <Form onSubmit={handleSubmit}>
                    <FormGridContainer>
                      <ResponsiveGridLayout
                        className={`layout ${!preview ? 'static-layout' : ''}`}
                        layouts={{ lg: event.form_layout }}
                        breakpoints={{
                          lg: 1200,
                          md: 996,
                          sm: 768,
                          xs: 480,
                          xxs: 0,
                        }}
                        cols={
                          preview
                            ? { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }
                            : { lg: 12, md: 12, sm: 1, xs: 1, xxs: 1 }
                        }
                        margin={[0, 0]}
                        rowHeight={30}
                        draggableHandle=".drag-handle"
                        onLayoutChange={onLayoutChange}
                        isDraggable={preview}
                        isResizable={preview}
                      >
                        {sortedFields.map((field, index) => {
                          const shouldShow =
                            !field.depends_on ||
                            !field.depends_value ||
                            values[field.depends_on] === field.depends_value;

                          if (!shouldShow) return null;

                          if (field.type === 'heading') {
                            return (
                              <div
                                key={field.field_id || field.name}
                                className="d-flex align-items-center"
                                style={{ padding: 12 }}
                              >
                                {preview && renderDragHandle()}
                                <h5 className="m-0 p-0">{field.label}</h5>
                              </div>
                            );
                          }

                          if (field.type === 'divider') {
                            return (
                              <div
                                key={field.field_id || field.name}
                                data-grid={event.form_layout.find(
                                  f => f.i === (field.field_id || field.name),
                                )}
                              >
                                {preview && (
                                  <div className="d-flex align-items-center">
                                    <hr style={{ flex: 1 }} className="mt-4 mb-4" />
                                    {renderDragHandle()}
                                    <hr style={{ flex: 1 }} className="mt-4 mb-4" />
                                  </div>
                                )}
                                {!preview && <hr className="mt-4 mb-4" />}
                              </div>
                            );
                          }

                          return (
                            <CustomFieldFormGroup
                              as={Col}
                              key={field.field_id || field.name}
                              data-grid={event.form_layout.find(
                                f => f.i === (field.field_id || field.name),
                              )}
                              preview={preview}
                            >
                              <span className="d-flex align-items-center justify-content-between mb-1">
                                <Form.Label
                                  style={{ fontSize: '0.8rem' }}
                                  className="m-0 p-0"
                                >
                                  <span className="d-flex align-items-center">
                                    {preview && renderDragHandle()}
                                    {field.label}
                                    {field.required && (
                                      <RequiredIndicator>*</RequiredIndicator>
                                    )}
                                  </span>
                                </Form.Label>
                                {preview && (
                                  <div className="d-flex align-items-center justify-content-center">
                                    {renderEditControls(field, index)}
                                  </div>
                                )}
                              </span>
                              {/* Render appropriate field type */}
                              {field.name && field.type === 'text' && (
                                <FormTextField
                                  name={field.name}
                                  placeholder={
                                    field.placeholder || field.label || 'Preencha o campo'
                                  }
                                />
                              )}

                              {field.name && field.type === 'textarea' && (
                                <FormTextAreaField
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  rows={2}
                                />
                              )}

                              {field.name && field.type === 'number' && (
                                <FormTextField
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  type="number"
                                />
                              )}

                              {field.name && field.type === 'email' && (
                                <FormTextField
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  type="email"
                                />
                              )}

                              {field.name && field.type === 'date' && (
                                <FormDateField
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                />
                              )}

                              {field.name && field.type === 'phone' && (
                                <FormMaskedInput
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  type="PHONE"
                                  mask={field.mask || '(99) 99999-9999'}
                                />
                              )}

                              {field.name && field.type === 'select' && (
                                <FormSelectField
                                  name={field.name}
                                  placeholder={field.placeholder || 'Selecione...'}
                                  options={field.options.map(opt => ({
                                    value: opt.value,
                                    label: opt.label,
                                  }))}
                                  isSearchable={field.options.length > 10}
                                  className="form-select-field"
                                  classNamePrefix="react-select"
                                />
                              )}

                              {field.name && field.type === 'radio' && (
                                <>
                                  {field.options.map((option, optionIndex) => (
                                    <Form.Check
                                      key={optionIndex}
                                      id={`${field.name}-${optionIndex}`}
                                      type="radio"
                                      label={option.label}
                                      name={field.name}
                                      value={option.value}
                                      checked={values[field.name] === option.value}
                                      onChange={() =>
                                        setFieldValue(field.name, option.value)
                                      }
                                      isInvalid={
                                        touched[field.name] && errors[field.name]
                                      }
                                    />
                                  ))}
                                  {touched[field.name] && errors[field.name] && (
                                    <Form.Control.Feedback
                                      type="invalid"
                                      style={{ display: 'block' }}
                                    >
                                      {errors[field.name]}
                                    </Form.Control.Feedback>
                                  )}
                                </>
                              )}

                              {field.name && field.type === 'cpf' && (
                                <FormMaskedInput
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  type="CPF"
                                  mask={field.mask || '999.999.999-99'}
                                />
                              )}

                              {field.name && field.type === 'cnpj' && (
                                <FormMaskedInput
                                  name={field.name}
                                  placeholder={field.placeholder || field.label}
                                  type="CNPJ"
                                  mask={field.mask || '99.999.999/9999-99'}
                                />
                              )}

                              {field.name && field.type === 'checkbox' && (
                                <>
                                  {field.options.map((option, optionIndex) => (
                                    <Form.Check
                                      key={optionIndex}
                                      id={`${field.name}-${optionIndex}`}
                                      type="checkbox"
                                      label={option.label}
                                      name={field.name}
                                      value={option.value}
                                      checked={
                                        Array.isArray(values[field.name]) &&
                                        values[field.name].includes(option.value)
                                      }
                                      onChange={e => {
                                        const currentValues = Array.isArray(
                                          values[field.name],
                                        )
                                          ? [...values[field.name]]
                                          : [];
                                        if (e.target.checked) {
                                          setFieldValue(field.name, [
                                            ...currentValues,
                                            option.value,
                                          ]);
                                        } else {
                                          setFieldValue(
                                            field.name,
                                            currentValues.filter(v => v !== option.value),
                                          );
                                        }
                                      }}
                                      isInvalid={
                                        touched[field.name] && errors[field.name]
                                      }
                                    />
                                  ))}
                                  {touched[field.name] && errors[field.name] && (
                                    <Form.Control.Feedback
                                      type="invalid"
                                      style={{ display: 'block' }}
                                    >
                                      {errors[field.name]}
                                    </Form.Control.Feedback>
                                  )}
                                </>
                              )}
                            </CustomFieldFormGroup>
                          );
                        })}
                      </ResponsiveGridLayout>
                    </FormGridContainer>
                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        type="submit"
                        variant="primary"
                        className="d-flex align-items-center"
                        disabled={!isValid || isSubmitting}
                        tabIndex={event.custom_fields.length + 1 /* Last tabIndex */}
                        onClick={handleSubmit}
                      >
                        {remainingSpots > 0
                          ? 'Confirmar inscrição'
                          : 'Entrar na lista de espera'}
                        <LuArrowRight className="ml-1" />
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </CardBody>
        </Card>
        {/* )} */}
      </>
    </Container>
  );
}

EventRegistrationPage.propTypes = {
  previewEvent: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    capacity: PropTypes.number,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    waiting_list_enabled: PropTypes.bool,
    custom_fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
          }),
        ),
        mask: PropTypes.string,
        placeholder: PropTypes.string,
        column_size: PropTypes.number,
        depends_on: PropTypes.string,
        depends_value: PropTypes.string,
      }),
    ),
  }),
  registrationCount: PropTypes.number,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onFetchEventPublic: PropTypes.func.isRequired,
  onCreateRegistration: PropTypes.func.isRequired,
  preview: PropTypes.bool,
  onFieldSelected: PropTypes.func,
};

EventRegistrationPage.defaultProps = {
  event: null,
  registrationCount: 0,
  isLoading: false,
  error: null,
  preview: false,
  previewEvent: null,
};

export default EventRegistrationPage;
